import cx from 'classnames';
import {map} from 'lodash';
import {useState, useContext, useMemo, useEffect} from 'react';
import {useTranslation} from 'react-i18next';

import {sessionHistoryApi} from 'api';
import {DataWrapper, Card, notifier} from 'components';
import {UserContext} from 'context';
import {useTask} from 'hooks';
import {SessionHistoryFullStatistic} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {settingsUtils} from 'utils';
import {getPercentage} from 'utils/transform';

import {REQUEST_SESSION_HISTORY_DATE_FORMAT} from '../constants';
import {CLIENT_DEVICE_METRICS_CONFIGS, ESCALATION_METRICS_CONFIGS, METRIC_CONFIG} from '../metric-config';
import {ActivityFilters, ActivityMetricConfig} from '../types';

import {ActivityMetricsFunnelChartCard} from './ActivityMetricsFunnelChartCard';
import {ActivityMetricsPieChart} from './ActivityMetricsPieChart';

const T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY.DASHBOARD;
const T_KEYS_STATISTICS_FAILED = TRANSLATION_KEYS.USER.ACTIVITY.ERROR.STATISTICS_FAILED;

type Props = {
    filters: ActivityFilters | null;
    isLoading: boolean;
    downloadFileDisabled?: boolean;
    onToggleLoading: (value: boolean) => void;
};

export const DashboardTotal = ({filters, isLoading, onToggleLoading}: Props) => {
    const {t} = useTranslation();
    const {settings} = useContext(UserContext);
    const [statistic, setStatistic] = useState<SessionHistoryFullStatistic>(null);
    const canSeeFilePipeline = useMemo(() => settingsUtils.canSeeFilePipeline(settings), [settings]);

    const availablePieChartMetricsConfigs: {configs: ActivityMetricConfig[], title: string}[] = useMemo(() => (
        [
            {configs: [METRIC_CONFIG.ANSWERED, METRIC_CONFIG.MISSED], title: t(METRIC_CONFIG.TOTAL.title)},
            {configs: [METRIC_CONFIG.SUCCESSFUL, METRIC_CONFIG.FAILED, METRIC_CONFIG.NO_RESULT], title: t(METRIC_CONFIG.ANSWERED.title)},
            ...(canSeeFilePipeline ?
                [{
                    configs: [
                        METRIC_CONFIG.ALL_FILES_PROCESSED,
                        METRIC_CONFIG.SOME_FILES_UNPROCESSED,
                        METRIC_CONFIG.NO_FILES_FOR_PROCESSING
                    ],
                    title: t(METRIC_CONFIG.ANSWERED.title)
                }] : []),
            {configs: CLIENT_DEVICE_METRICS_CONFIGS, title: t(T_KEYS.LABELS.CLIENT_DEVICES)},
            {configs: ESCALATION_METRICS_CONFIGS, title: t(METRIC_CONFIG.PRE_SESSION_FLOW.title)}
        ]
    ), [t, canSeeFilePipeline]);

    const allStatisticConfigs: ActivityMetricConfig[] = useMemo(() => ([
        METRIC_CONFIG.ANSWERED, METRIC_CONFIG.MISSED,
        METRIC_CONFIG.SUCCESSFUL, METRIC_CONFIG.FAILED,
        ...(canSeeFilePipeline ? [METRIC_CONFIG.ALL_FILES_PROCESSED, METRIC_CONFIG.SOME_FILES_UNPROCESSED] : [])
    ]), [canSeeFilePipeline]);

    const {startTask} = useTask(sessionHistoryApi.getShortActivityStatistics, {
        onComplete: setStatistic,
        onToggleLoading,
        onError: () => notifier.error({title: t(T_KEYS_STATISTICS_FAILED)})
    });

    useEffect(() => {
        if (startTask && filters) {
            startTask({
                ...filters,
                startDate: filters.startDate.format(REQUEST_SESSION_HISTORY_DATE_FORMAT),
                endDate: filters.endDate.format(REQUEST_SESSION_HISTORY_DATE_FORMAT)
            });
        }
    }, [filters, startTask]);

    if (!filters) {
        return null;
    }

    return (
        <div className='tw-relative tw-min-h-32'>
            <DataWrapper isLoading={isLoading} hideContentOnLoading={false} noData={!statistic}>
                {statistic &&
                    <Card>
                        <div className='tw-ts-sub-header tw-mb-4'>{t(T_KEYS.LABELS.PIE_CHARTS)}</div>
                        <div className={cx('tw-grid tw-gap-5 tw-grid-cols-1 md:tw-grid-cols-2', {
                            'xl:tw-grid-cols-3': availablePieChartMetricsConfigs.length === 3
                        })}
                        >
                            {map(availablePieChartMetricsConfigs, ({title, configs}, i) => (
                                <div key={i}>
                                    <ActivityMetricsPieChart
                                        title={title}
                                        statistic={statistic.total}
                                        configs={configs}
                                    />
                                </div>
                            ))}
                        </div>
                    </Card>}
                {statistic &&
                    <div className='tw-my-4 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-overflow-auto tw-gap-5'>
                        <Card>
                            <div className='tw-ts-sub-header tw-mb-4'>{t(T_KEYS.LABELS.ALL_STATISTIC)}</div>
                            <div className='tw-flex tw-justify-between'>
                                <div className='tw-ts-base-bold lg:tw-ts-h5'>{t(METRIC_CONFIG.TOTAL.title)}</div>
                                <div className='tw-ts-base-bold lg:tw-ts-h3'>{METRIC_CONFIG.TOTAL.valueGetter(statistic.total)}</div>
                            </div>
                            {allStatisticConfigs.map(({metric, title, valueGetter, totalGetter}) => {
                                const value = valueGetter(statistic.total);
                                return (
                                    <div key={metric} className='tw-flex tw-justify-between tw-mt-4'>
                                        <div className='tw-ts-base lg:md:tw-ts-h5'>{t(title)}</div>
                                        <div className='tw-ts-base lg:md:tw-ts-h3'>
                                            {value}
                                            <span>{` (${getPercentage(value, totalGetter(statistic.total))}%)`}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </Card>
                        <ActivityMetricsFunnelChartCard statistic={statistic} className='tw-flex-grow tw-overflow-hidden' />
                    </div>}
            </DataWrapper>
        </div>
    );
};
